import { inject } from '@vercel/analytics'
inject()

import { auth } from './lib/supabase.js'
import { AuthComponent } from './components/Auth.js'
import { GameComponent } from './components/Game.js'
import { ModalsComponent } from './components/Modals.js'
import { LandingComponent } from './components/Landing.js'

class LyricGuesserApp {
  constructor() {
    this.user = null
    this.gameComponent = null
    this.authComponent = null
    this.modalsComponent = null
    this.statsModal = null
    this.landingComponent = null
    this.isInitialized = false
    this.hasSeenLanding = false
    
    this.init()
  }

  async init() {
    try {
      // Default to dark mode
      document.body.classList.add('dark-mode');

      this.showLoading(true)
      
      // Check for existing user session
      await this.checkAuth()
      
      // Check if user has already played (localStorage flag)
      this.hasSeenLanding = sessionStorage.getItem('songola_has_played') === 'true'
      
      // Show landing page for new visitors, game for returning players
      if (!this.hasSeenLanding && !this.user) {
        this.showLanding()
      } else if (this.shouldShowAuth()) {
        this.showAuth()
      } else {
        await this.showGame()
      }
      
      // Setup auth state listener
      this.setupAuthListener()
      
      this.isInitialized = true
    } catch (error) {
      console.error('Failed to initialize app:', error)
      this.showError('Failed to load application. Please refresh and try again.')
    } finally {
      this.showLoading(false)
    }
  }

  async checkAuth() {
    try {
      this.user = await auth.getUser()
    } catch (error) {
      console.log('No authenticated user found')
      this.user = null
    }
  }

  showLanding() {
    const appContainer = document.getElementById('app')
    appContainer.innerHTML = ''
    
    // Destroy existing components
    this.destroyAllComponents()
    
    // Create landing component
    this.landingComponent = new LandingComponent(appContainer, (destination) => {
      if (destination === 'auth') {
        this.showAuth()
      } else if (destination === 'game') {
        // Set flag that user has played
        sessionStorage.setItem('songola_has_played', 'true')
        this.showGame()
      }
    })
  }

  async showDashboard() {
    const appContainer = document.getElementById('app')
    appContainer.innerHTML = ''
    
    const { DashboardComponent } = await import('./components/Dashboard.js')
    
    const dashboard = new DashboardComponent(appContainer, this.user, (destination) => {
      if (destination === 'game') {
        this.showGame()
      } else if (destination === 'auth') {
        this.showAuth()
      } else if (destination === 'stats') {
        this.showStatsModal()
      }
    })
  }

  shouldShowAuth() {
    // For now, we'll allow guest access
    return false
  }

  setupAuthListener() {
    auth.onAuthStateChange((event, session) => {
      const newUser = session?.user || null
      
      if (newUser?.id !== this.user?.id) {
        this.user = newUser
        
        if (this.isInitialized) {
          // Re-initialize the appropriate view
          if (this.shouldShowAuth() && !this.user) {
            this.showAuth()
          } else {
            this.showGame()
          }
        }
      }
    })
  }

  async showGame() {
    const appContainer = document.getElementById('app')
    appContainer.innerHTML = ''
    
    // Destroy landing component if it exists
    if (this.landingComponent) {
      this.landingComponent.destroy()
      this.landingComponent = null
    }
    
    // Create game component
    this.gameComponent = new GameComponent(appContainer, this.user)
    
    // Setup game component callbacks
    this.gameComponent.onSignInRequest = () => this.showAuth()
    this.gameComponent.onSignOutRequest = () => this.showGame()
    this.gameComponent.onDashboardRequest = () => this.showDashboard()
    
    // Create modals component
    this.modalsComponent = new ModalsComponent(
      (genre) => this.gameComponent.setGenre(genre),
      (date) => this.gameComponent.setGameDate(date)
    )
    
    // Setup modal callbacks
    this.gameComponent.onGenreModalRequest = () => this.modalsComponent.showGenreModal()
    this.gameComponent.onArchiveModalRequest = () => this.modalsComponent.showArchiveModal()
    this.gameComponent.onStatsModalRequest = () => this.showStatsModal()
  }

  showAuth() {
    const appContainer = document.getElementById('app')
    appContainer.innerHTML = ''
    
    // Destroy existing components
    this.destroyAllComponents()
    
    // Create auth component
    this.authComponent = new AuthComponent(appContainer, (user) => {
      this.user = user
      this.showGame()
    })
  }

  destroyAllComponents() {
    if (this.gameComponent) {
      this.gameComponent = null
    }
    if (this.modalsComponent) {
      this.modalsComponent.destroy()
      this.modalsComponent = null
    }
    if (this.statsModal) {
      this.statsModal.destroy()
      this.statsModal = null
    }
    if (this.landingComponent) {
      this.landingComponent.destroy()
      this.landingComponent = null
    }
  }

  async showStatsModal() {
    if (!this.gameComponent) {
      alert('📊 No stats available yet. Play some games first!')
      return
    }

    try {
      let stats = this.gameComponent.stats || {
        gamesPlayed: 0,
        gamesWon: 0,
        gamesCompleted: 0,
        winPercentage: 0,
        currentStreak: 0,
        maxStreak: 0
      }
      let gameHistory = []

      if (this.user) {
        // Existing user stats logic
        try {
          const { db } = await import('./lib/supabase.js')
          const userStats = await db.getUserStats(this.user.id)
          if (userStats) {
            stats = userStats
          }
          const userGameHistory = await db.getUserGameResults(this.user.id, 10)
          if (userGameHistory && Array.isArray(userGameHistory)) {
            gameHistory = userGameHistory
          }
        } catch (dbError) {
          console.warn('Failed to load detailed stats from database:', dbError)
        }
      } else {
        // Guest user stats from localStorage
        const { localStorageManager } = await import('./lib/localStorage.js')
        stats = localStorageManager.getGuestStats()
        gameHistory = localStorageManager.getGuestGameHistory().slice(0, 10)
        console.log('Using guest stats from localStorage')
      }

      // Create and show stats modal
      const { StatsModal } = await import('./components/StatsModal.js')
      if (this.statsModal) {
        this.statsModal.destroy()
      }
      
      this.statsModal = new StatsModal(stats, gameHistory)
      this.statsModal.show()
      
    } catch (error) {
      console.error('Failed to show stats modal:', error)
      alert('📊 Unable to load stats at the moment. Please try again.')
    }
  }

  showLoading(show) {
    const loadingElement = document.getElementById('loading')
    if (loadingElement) {
      loadingElement.style.display = show ? 'flex' : 'none'
    }
  }

  showError(message) {
    const appContainer = document.getElementById('app')
    appContainer.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: 20px;
        text-align: center;
      ">
        <h1 style="color: var(--error-color); margin-bottom: 16px;">⚠️ Error</h1>
        <p style="margin-bottom: 24px; color: var(--text-secondary);">${message}</p>
        <button 
          class="btn btn-primary" 
          onclick="window.location.reload()"
        >
          Refresh Page
        </button>
      </div>
    `
  }

  // Utility methods for development/debugging
  async signOut() {
    try {
      await auth.signOut()
      this.user = null
      this.showGame()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  getCurrentUser() {
    return this.user
  }

  getGameComponent() {
    return this.gameComponent
  }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Make app instance globally available for debugging
  window.lyricGuesserApp = new LyricGuesserApp()
})

// Global error handling
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error)
  // You could send this to an error reporting service
})

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  // You could send this to an error reporting service
})

// Export for potential module usage
export { LyricGuesserApp }